import * as pageActions from 'client/actions/pageActions'
import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { UrlRedirect } from 'client/components/core/Sidebar/components/Settings/options'
import { mimeTypes } from 'client/constants/editorSettings'
import { AutomationRuleTriggersEnum } from 'client/enums/AutomationRulesEnum'
import { useAutomationRules, usePage } from 'client/store'
import {
  saveAutomationRule,
  saveDeletedAutomationRule,
} from 'client/store/automationRules/automationRulesActions'
import automationRulesSelectors from 'client/store/automationRules/automationRulesSelectors'
import { DataFile } from 'client/store/files/filesReducer'
import pageSelectors from 'client/store/page/pageSelectors'
import { automationRuleTriggerEntityProperties } from 'client/types/AutomationRulesActionsInterface'
import { blogPageTypes, pageTypes } from 'common/constants/pageTypes'
import { buttonActionsTypes } from 'common/constants/settings'
import {
  RedirectionTypesEnum,
  sendFormRedirectionChoices,
} from 'common/enums/ButtonActionEnum'
import {
  ButtonActionTypeEnum,
  buttonActionsNames,
  getButtonActions,
} from 'common/enums/ButtonActionTypeEnum'
import { buttonLinkTypeNames } from 'common/enums/ButtonLinkEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { addUrlSchemaToTheLink } from 'common/utils/urlUtils'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { CommonButtonSettings } from 'src/editor/shared/entities/common-button-settings'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { isWebsitePage } from 'src/shared/utils/is-website-page'
import { FileUpload } from '../../components'
import AutomationActionsList from '../../components/AutomationRulesSettings/AutomationActionsList'
import AutomationRulesActionsModal from '../../components/AutomationRulesSettings/AutomationRulesActionsModal'
import automationRulePageTypes from '../../components/AutomationRulesSettings/utils/automationRulesPageTypes'
import BlogLinkAction from '../../components/BlogLink'
import PrimaryButton from '../../components/PrimaryButton'

export interface ButtonSettingsProps {
  entity: ButtonInterface
}

const ButtonSettingsNew = ({ entity }: ButtonSettingsProps) => {
  const { t } = useTranslation()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ButtonInterface>()
  const dispatch = useDispatch()
  const popups = usePage(pageSelectors.getWebsiteGlobalOrPagePopupEntities)(
    entity,
  )
  const pageType = usePage(pageSelectors.getPageType)
  const isTemplate = usePage(pageSelectors.isPageTemplate)
  const doubleOptIn = usePage(pageSelectors.getDoubleOptIn)
  const toggleDoubleOptInAction = useActions(pageActions.toggleDoubleOptIn)
  const [isAutomationsModalOpen, setIsAutomationsModalOpen] = useState(false)
  const originalButtonActions = useAutomationRules(state =>
    automationRulesSelectors.getOriginalAutomationRulesByButtonIdId(
      state,
      entity.id,
    ),
  )

  const editedButtonActions = useAutomationRules(state =>
    automationRulesSelectors.getEditedAutomationRulesByEntityId(
      state,
      entity.id,
      automationRuleTriggerEntityProperties.buttonId,
    ),
  )
  const {
    text: { paragraphTextColor },
  } = useThemeColor()
  const updateAction = (action: ButtonActionTypeEnum) => {
    const popup = action !== ButtonActionTypeEnum.ShowPopup ? '' : entity.popup

    const needResetRedirectionType =
      action === ButtonActionTypeEnum.SendForm &&
      entity.action !== ButtonActionTypeEnum.SendForm

    const newRedirectionType = blogPageTypes.includes(pageType)
      ? RedirectionTypesEnum.customUrl
      : RedirectionTypesEnum.nextStep

    const updatedEntity = {
      ...entity,
      action,
      redirectionType: needResetRedirectionType
        ? newRedirectionType
        : entity.redirectionType,
      popup,
    }

    const needResetFile =
      action === ButtonActionTypeEnum.DownloadFile &&
      entity.action !== ButtonActionTypeEnum.DownloadFile
    if (needResetFile) {
      delete updatedEntity.fileId
    }

    const needResetTagId =
      action !== ButtonActionTypeEnum.SendForm && !!entity.tagId

    if (needResetTagId) {
      delete updatedEntity.tagId
    }

    const needResetBlogLink =
      entity.action === ButtonActionTypeEnum.BlogLink &&
      action !== ButtonActionTypeEnum.BlogLink

    if (needResetBlogLink) {
      updatedEntity.blogLink = ''
    }

    const needResetAutomationRules = action !== ButtonActionTypeEnum.SendForm
    // check if we have existing rules
    resetAutomationRules(
      needResetAutomationRules && editedButtonActions.length > 0,
    )
    updateEntity(updatedEntity)
  }

  const toggleDoubleOptIn = () => {
    toggleDoubleOptInAction()
  }

  const removeFileId = () => {
    const updatedEntity = { ...entity }
    delete updatedEntity.fileId
    updateEntity(updatedEntity)
  }

  const updatePopup = (popupId: string) => {
    updateProp('popup')(popupId)
  }

  const toggleNoFollow = (value: boolean) => {
    let noFollow = ''
    if (value) {
      noFollow = 'nofollow'
    }
    updateProp('rel')(noFollow)
  }

  const updateBlogLink = (value: string) => {
    const updatedEntity = {
      ...entity,
      blogLink: value,
    }
    updateEntity(updatedEntity)
  }

  const updateRedirectionType = (redirectionType: RedirectionTypesEnum) => {
    const updatedEntity = {
      ...entity,
      urlRedirect: '',
      redirectionType,
    }
    updateEntity(updatedEntity)
  }
  const popupChoiceList = popups.reduce(
    (popupChoices, popup) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...popupChoices,
      [popup.id]:
        popup.type === EntityTypeEnum.Popup
          ? popup?.title
          : popup?.options?.title,
    }),
    {},
  )

  const resetAutomationRules = (condition: boolean) => {
    if (condition) {
      const automationRuleDeletedData = {
        automationTriggers: [
          {
            type: automationTriggerType,
            buttonId: entity.id,
          },
        ],
        automationActions: originalButtonActions,
      }
      const automationRuleData = {
        automationTriggers: [
          {
            type: automationTriggerType,
            buttonId: entity.id,
          },
        ],
        automationActions: [],
      }
      dispatch(saveDeletedAutomationRule(automationRuleDeletedData))
      dispatch(saveAutomationRule(automationRuleData))
    }
  }

  const isActionSendForm = entity.action === ButtonActionTypeEnum.SendForm
  const isRedirectionTypeCustomUrl =
    entity.redirectionType === RedirectionTypesEnum.customUrl
  const isActionBlogLink = entity.action === ButtonActionTypeEnum.BlogLink

  const pageButtonActions = getButtonActions(pageType)
  const availableButtonActions = pageButtonActions.reduce(
    (actions, action) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...actions,
      [action]: buttonActionsNames[action],
    }),
    {} as Record<ButtonActionTypeEnum, string>,
  )

  const redirectionTypeNames = { ...sendFormRedirectionChoices }
  if (blogPageTypes.includes(pageType) || isWebsitePage(pageType)) {
    delete redirectionTypeNames[RedirectionTypesEnum.nextStep]
  }

  const automationTriggerType = blogPageTypes.includes(pageType)
    ? AutomationRuleTriggersEnum.blog_page_form_subscribed
    : AutomationRuleTriggersEnum.form_subscribed

  const isAutomationRulesVisible =
    !isTemplate &&
    automationRulePageTypes.includes(pageType) &&
    isActionSendForm

  useEffect(() => {
    if (
      entity.action === buttonActionsTypes.nextStepRedirection &&
      blogPageTypes.includes(pageType)
    ) {
      updateEntity({
        ...entity,
        action: ButtonActionTypeEnum.SendForm,
        redirectionType: RedirectionTypesEnum.customUrl,
      })
    }
  }, [])

  return (
    <React.Fragment>
      {!!pageButtonActions.length && (
        <Select
          update={updateAction}
          choiceList={availableButtonActions}
          labelText="Button action"
          value={entity.action}
        />
      )}
      {entity.action === buttonActionsTypes.downloadFile && (
        <FileUpload
          fileId={entity.fileId}
          afterUpload={(file: DataFile) => updateProp('fileId')(file.id)}
          removeFile={removeFileId}
          mimeTypes={mimeTypes.all}
        />
      )}
      {entity.action === buttonActionsTypes.showPopup && (
        <Select
          labelText="Popup"
          update={updatePopup}
          choiceList={popupChoiceList}
          firstOption="entity_settings.button.choose_actions"
          value={entity.popup}
          skipTranslation
        />
      )}
      {entity.action === buttonActionsTypes.openUrl && [
        <TextInputControl
          key="url"
          wrapperProps={{
            labelText: 'url',
          }}
          update={updateProp('linkUrl')}
          placeholder={t('entity_settings.button.url_input.placeholder')}
          value={entity.linkUrl}
          onBlur={() =>
            updateProp('linkUrl')(addUrlSchemaToTheLink(entity.linkUrl ?? ''))
          }
          removeWhitespaces
        />,
        <Select
          key="link-type"
          labelText="open in new window"
          update={updateProp('linkTarget')}
          choiceList={buttonLinkTypeNames}
          value={entity.linkTarget}
        />,
        <CheckboxControl
          key="noFollow"
          label="entity_settings.button.no_follow.label"
          tooltipText="entity_settings.button.no_follow.description"
          value={Boolean(entity.rel)}
          update={toggleNoFollow}
        />,
      ]}
      {isActionSendForm && (
        <>
          <Select
            labelText="entity_settings.button.send_form.redirection.label"
            value={entity.redirectionType}
            update={updateRedirectionType}
            dataTestElement="button-redirection-checkbox"
            choiceList={redirectionTypeNames}
          />
          {entity.redirectionType === RedirectionTypesEnum.none && (
            <>
              <TextInputControl
                wrapperProps={{
                  labelText:
                    'entity_settings.button.send_form.no_redirection.title',
                }}
                value={entity.optInSuccessText}
                update={updateProp('optInSuccessText')}
              />
              <ColorPickerControl
                label="text color"
                update={updateProp('optInSuccessTextColor')}
                color={entity.optInSuccessTextColor || paragraphTextColor}
              />
            </>
          )}
        </>
      )}
      {blogPageTypes.includes(pageType) && isActionBlogLink && (
        <React.Fragment>
          <BlogLinkAction
            update={updateBlogLink}
            blogLink={entity.blogLink as string}
            needOptionWrapper
          />
          <Select
            labelText="open in new window"
            update={updateProp('linkTarget')}
            choiceList={buttonLinkTypeNames}
            value={entity.linkTarget}
          />
          <CheckboxControl
            label="entity_settings.button.no_follow.label"
            tooltipText="entity_settings.button.no_follow.description"
            value={Boolean(entity.rel)}
            update={toggleNoFollow}
          />
        </React.Fragment>
      )}
      {isActionSendForm && isRedirectionTypeCustomUrl && (
        <UrlRedirect
          key="urlRedirect"
          urlRedirect={addUrlSchemaToTheLink(entity.urlRedirect)}
          update={updateProp('urlRedirect')}
          data-test-element="button-redirection-input"
        />
      )}
      {isActionSendForm && pageType !== pageTypes.webinarRegistration && (
        <CheckboxControl
          label="entity_settings.button.send_model.double_opt_in.label"
          value={doubleOptIn}
          update={toggleDoubleOptIn}
        />
      )}
      {isAutomationRulesVisible && (
        <>
          {editedButtonActions.length > 0 && (
            <AutomationActionsList actions={editedButtonActions} />
          )}
          <PrimaryButton
            onClick={() => {
              setIsAutomationsModalOpen(true)
            }}
            key={'automationSettingsButton'}
          >
            {t('entity_settings.automation_rule.edit.add_automation_rule')}
          </PrimaryButton>
          <AutomationRulesActionsModal
            key="automationSettingsModal"
            opened={isAutomationsModalOpen}
            triggerType={automationTriggerType}
            onClose={() => setIsAutomationsModalOpen(false)}
            //@ts-expect-error @TODO check
            actions={editedButtonActions}
            entityId={entity.id}
            triggerKey={'buttonId'}
          />
        </>
      )}
      <CommonButtonSettings entity={entity} />
    </React.Fragment>
  )
}

export default ButtonSettingsNew
